import type { ReactNode } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { Label } from '../tailgrids/core/label.js';
import { Description } from '../tailgrids/core/description.js';
import { FieldError } from '../tailgrids/core/field.js';
import { HelpIcon } from '../ui/HelpIcon.js';

/** Props every form field binder takes. */
export interface FormFieldBaseProps<TValues extends FieldValues = FieldValues> {
  /** Path of the value in the form, e.g. `name` or `limits.maxSteps`. */
  name: FieldPath<TValues>;
  label: ReactNode;
  /** One line under the field: what it is for, valid values. */
  description?: ReactNode;
  /** Longer help behind the (?) icon, from `client/src/help/*-fields.ts`. */
  help?: string;
  /** Shows the required mark. Whether the value is required comes from the schema. */
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/** What an empty input writes to the form: `undefined` (unset, the default), `null` (clear), or `''`. */
export type EmptyAs = undefined | null | '';

export function FormLabel({ label, required, help }: { label: ReactNode; required?: boolean; help?: string }) {
  return (
    <span className="flex items-center gap-1">
      <Label className="flex items-center gap-0.5 text-sm font-medium text-input-label-text">
        {label}
        {required && (
          <span aria-hidden="true" className="text-error-500">
            *
          </span>
        )}
      </Label>
      {help && <HelpIcon text={help} />}
    </span>
  );
}

export function FormDescription({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return <Description className="text-xs text-text-tertiary">{children}</Description>;
}

/** The field's error, read by react-aria from the field's `isInvalid` and announced to screen readers. */
export function FormFieldError({ message }: { message?: string }) {
  return <FieldError className="text-xs">{message}</FieldError>;
}
